import type { GetStaticPathsResult } from 'next';
import { ConfigProvider } from 'shared/config/ConfigContext';
import fhhData from 'shared/seo/data/fhh.json';
import { LinkTree } from 'shared/seo/LinkTree';
import { PopularCities } from 'shared/seo/PopularCities';
import { DestinationPage as OldDestinationPage } from '@/components/DestinationPage';
import { cityLevel1Factory, vacationRentalSearchFactory } from '@/components/factory';
import { DestinationPage as NewDestinationPage } from '@/components/NewDestinationPage';
import { type OfferItem, Offers } from '@/components/offers/Offers';
import { config, feConfig } from '@/config';
import { fhhCities, fhhCountries, fhhMapping } from '@/data';
import { verticals } from '@/data/verticals';
import type { CityPageWithOffersProps, SeoPageContext } from '@/interfaces/pages';
import { fetchAutocompleteBounds } from '@/lib/autocompleteBounds';
import { fetchJson } from '@/lib/fetchJson';
import { pageContentUrl, pageFor } from '@/lib/fhhContent';
import { fetchPagesMetadata } from '@/lib/pages-metadata';

const fhhOfferUrl = config.get('fhh.offers');
const baseApiUrl = `${fhhOfferUrl}?no_of_beds_from=2&mobile=false&nrFUSAds=0`;
const allPagesMetadataUrl = config.get('fhh.allCountryPlaceDestinationPagesMetadata');

const fetchOffers = (query: string): Promise<OfferItem[]> =>
    fetchJson<{ resultItems?: OfferItem[] }>(`${baseApiUrl}&${query}`)
        .then((json) => json.resultItems ?? [])
        .catch((err) => {
            console.warn(err);
            return [] as OfferItem[];
        });

export const getStaticPaths = async (): Promise<GetStaticPathsResult> => {
    const allPagesMetadata: Record<string, string>[] = await fetchPagesMetadata(allPagesMetadataUrl).catch((err) => {
        console.error(err);
        return err;
    });

    const destinations = allPagesMetadata.map((page: { place: string; country: string }) => ({
        params: {
            cityLevel1: page.place,
            countryLevel1: page.country,
        },
    }));

    return {
        paths: destinations,
        fallback: false,
    };
};

export const getStaticProps = async (context: SeoPageContext) => {
    const { countryLevel1, cityLevel1 } = context.params;
    const page = await pageFor(pageContentUrl.forCountryPlaceDestination(countryLevel1, cityLevel1));

    const destination = fhhCities.find(
        (place) => place.cityPath === cityLevel1.toLowerCase() && place.countryPath === countryLevel1.toLowerCase(),
    );
    const destinationCountry = fhhCountries.find((place) => place.countryPath === countryLevel1.toLowerCase());

    if (!page.htmlContent && (!destination || !destinationCountry)) {
        return { notFound: true };
    }

    const popularCities = fhhMapping[destination.id] ?? fhhMapping[destinationCountry.id] ?? [];
    const searchType = destination.country.toLowerCase() === 'norge' ? 'municipality' : 'locality';

    const [offersByName, bounds] = await Promise.all([
        fetchOffers(`country=${destination.country}&${searchType}=${destination.city}`),
        fetchAutocompleteBounds(config.get('fhh.autocomplete'), `${destination.city}, ${destination.country}`),
    ]);

    // Some places with a dedicated seo-page (e.g. Drøbak) has a different municipality, so the
    // query by municipality above wont return results. We fallback to a bounds-query when the offersByName returns nothing.
    const offers =
        offersByName.length === 0 && bounds
            ? await fetchOffers(
                  `country=${destination.country}` +
                      `&lat_sw=${bounds.southwest_lat}&lng_sw=${bounds.southwest_lng}` +
                      `&lat_ne=${bounds.northeast_lat}&lng_ne=${bounds.northeast_lng}`,
              )
            : offersByName;

    return {
        props: {
            destination: { ...destination, bounds },
            content: page.htmlContent ?? null,
            popularCities,
            offers,
            config: feConfig,
        },
        revalidate: 3600,
    };
};

const CityLevel1 = ({ destination, content, popularCities, offers, config }: CityPageWithOffersProps) => {
    const factory = cityLevel1Factory(destination, fhhData, vacationRentalSearchFactory());
    const DestinationPage = content ? NewDestinationPage : OldDestinationPage;

    return (
        <ConfigProvider config={config}>
            <DestinationPage factory={factory} vertical={verticals.fhh} data={fhhData}>
                {content ? <div className="mb-64" dangerouslySetInnerHTML={{ __html: content }} /> : null}
                <Offers
                    destination={destination.city}
                    offers={offers}
                    vertical={verticals.fhh}
                    heading={fhhData.lang.headingOffers}
                    showMap={false}
                />
                <PopularCities
                    destination={destination}
                    cities={popularCities}
                    cdnUrl={fhhData.cdnPath}
                    verticalUrl={fhhData.basePath}
                    lang={fhhData.lang}
                />
                <LinkTree
                    heading={fhhData.lang.linkTree.heading}
                    description={fhhData.lang.linkTree.description}
                    data={fhhData.linkTree}
                    url={fhhData.basePath}
                />
            </DestinationPage>
        </ConfigProvider>
    );
};

export default CityLevel1;
